import { apiFetch } from './client';

/**
 * The org's subscription state, as returned by GET /api/billing/status.
 *
 * Note the server returns a REDUCED shape when the user has no organization —
 * billing.controller.ts#getStatus short-circuits to `{ plan, subscriptionStatus,
 * subscriptionActive, currentPeriodEnd }` only. The count/array fields are therefore
 * optional-in-practice; `fetchBillingStatus` normalises them so callers never have to
 * guess whether `activeAddons` exists.
 */
export interface BillingStatus {
  plan: string;
  subscriptionStatus: string;
  subscriptionActive: boolean;
  currentPeriodEnd: string | null;
  billingCycle: string | null;
  isTrialing?: boolean;
  trialEndDate?: string | null;
  extraAiCredits: number;
  extraStaffCount: number;
  extraLocationCount: number;
  aiCreditsUsed: number;
  activeAddons: string[];
  hasStaffSubscription: boolean;
  hasLocationSubscription: boolean;
}

/** The server's no-organization response omits everything below `currentPeriodEnd`. */
type RawBillingStatus = Partial<BillingStatus> &
  Pick<BillingStatus, 'plan' | 'subscriptionStatus' | 'subscriptionActive' | 'currentPeriodEnd'>;

export async function fetchBillingStatus(): Promise<BillingStatus> {
  const res = await apiFetch<{ success: boolean; data?: RawBillingStatus }>('/api/billing/status');
  const d = res.data;
  if (!d) throw new Error('Failed to load billing status');

  // Normalise the no-org shape into the full one, so consumers can read
  // `extraLocationCount` / `activeAddons` unconditionally.
  return {
    plan: d.plan,
    subscriptionStatus: d.subscriptionStatus,
    subscriptionActive: d.subscriptionActive,
    currentPeriodEnd: d.currentPeriodEnd,
    billingCycle: d.billingCycle ?? null,
    isTrialing: d.isTrialing,
    trialEndDate: d.trialEndDate ?? null,
    extraAiCredits: d.extraAiCredits ?? 0,
    extraStaffCount: d.extraStaffCount ?? 0,
    extraLocationCount: d.extraLocationCount ?? 0,
    aiCreditsUsed: d.aiCreditsUsed ?? 0,
    activeAddons: d.activeAddons ?? [],
    hasStaffSubscription: d.hasStaffSubscription ?? false,
    hasLocationSubscription: d.hasLocationSubscription ?? false,
  };
}
